// Example data for the landing-page preview and the /sample-report page.
// It has exactly the same shape as a real /api/faults response: DVSA vehicle
// details and MOT tests, plus the AI fault analysis.

export const SAMPLE_REGISTRATION = "SA16MPL";
export const SAMPLE_MILEAGE = 77200;

const OIL_LEAK = { text: "Oil leak, but not excessive (8.4.1 (a) (i))", type: "ADVISORY", dangerous: false };

export const SAMPLE_REPORT = {
  vehicleInfo: {
    make: "FORD",
    model: "FOCUS",
    firstUsedDate: "2016-03-18",
    fuelType: "Petrol",
    engineSize: "999",
    primaryColour: "Blue",
    registrationDate: "2016-03-18",
    manufactureDate: "2016-03-18",
    hasOutstandingRecall: "No",
  },

  riskScore: 46,

  kpis: {
    reliability: "6.8/10",
    runningCosts: "££",
    avgRepair: "£420",
    bestYears: "2015-2018",
  },

  summary: {
    overallAssessment:
      "A typical ten-year-old Focus for its mileage, with a steady MOT record and one fail in 2024 for a tyre and headlamp aim. The main concern is an oil leak that has been advised at every MOT since 2022. The cooling system and the timing belt are also worth checking on this engine.",
    keyRisks: [
      "Oil leak advised at every MOT since 2022",
      "Coolant leaks are common on the 1.0 EcoBoost",
      "Timing belt is due by age",
    ],
    recommendations: [
      "Ask for invoices for the timing belt and any coolant hose work",
      "Check under the car for fresh oil after the test drive",
      "Budget for front tyres, pads and discs in the next year",
      "Use the repeated oil-leak advisory to negotiate on price",
    ],
  },

  faults: [
    {
      problem: "Oil leak from cam cover or timing cover",
      location: "Top and front of the engine",
      symptoms:
        "An oil-leak advisory has appeared at every MOT since 2022. With the engine cold, check around the cam cover gasket and down the timing cover for wet, dark residue. Look under the car for fresh drips after the test drive, and for a burning-oil smell from the bonnet.",
      recommendedFix: "Replace the cam cover gasket and reseal the timing cover.",
      costEstimate: "£180-450",
      severityGrade: 3,
      likelihood: "High",
      riskFactor: 78,
    },
    {
      problem: "Coolant hose or expansion tank leak",
      location: "Engine cooling system",
      symptoms:
        "The 1.0 EcoBoost is known for coolant leaks from its hoses and expansion tank. With the engine cold, check the level sits between MIN and MAX. Look for pink or orange crust around the hose joints and tank seams. A sweet smell after driving, or a temperature gauge that climbs in traffic, are warning signs.",
      recommendedFix: "Replace the leaking hose or expansion tank, then refill and bleed the system.",
      costEstimate: "£250-400",
      severityGrade: 4,
      likelihood: "Medium",
      riskFactor: 72,
    },
    {
      problem: "Front brake discs and pads worn",
      location: "Front brakes",
      symptoms:
        "Brake wear was advised at the 2023 and 2026 MOTs. Look through the wheel for a raised lip on the edge of the disc and deep scoring. On the test drive, listen for grinding and feel for judder through the pedal or steering wheel when braking.",
      recommendedFix: "Replace the front discs and pads as a set.",
      costEstimate: "£180-260",
      severityGrade: 3,
      likelihood: "High",
      riskFactor: 70,
    },
    {
      problem: "Rear suspension bush wear",
      location: "Rear suspension arms",
      symptoms:
        "A worn bush was advised at the 2025 MOT. Drive slowly over speed bumps and listen for knocks from the rear. Check the rear tyres for uneven wear on the inner edge.",
      recommendedFix: "Replace the worn bushes or the complete arm, then check the wheel alignment.",
      costEstimate: "£150-320",
      severityGrade: 2,
      likelihood: "High",
      riskFactor: 64,
    },
    {
      problem: "Tyre wear",
      location: "Tyres",
      symptoms:
        "Tyres close to the legal limit were advised in 2020 and 2026, and a rear tyre failed in 2024. Check the tread across the full width of each tyre (the legal minimum is 1.6mm) and look for wear on the inner edges.",
      recommendedFix: "Replace worn tyres in pairs and check the wheel alignment.",
      costEstimate: "£120-220",
      severityGrade: 2,
      likelihood: "High",
      riskFactor: 60,
    },
    {
      problem: "Timing belt due by age",
      location: "Engine timing belt (runs in oil)",
      symptoms:
        "This engine's timing belt runs in oil and is usually due at around 10 years or 125,000 miles, whichever comes first. Ask for an invoice showing it has been changed. Black specks on the dipstick or a low oil pressure warning can mean the belt is breaking down.",
      recommendedFix: "Replace the timing belt, tensioner and oil pump pick-up strainer.",
      costEstimate: "£700-1100",
      severityGrade: 4,
      likelihood: "Medium",
      riskFactor: 58,
    },
    {
      problem: "Clutch and flywheel wear",
      location: "Clutch and gearbox",
      symptoms:
        "At this mileage the clutch may be near the end of its life. Signs include a high biting point, the engine revving without the car speeding up in higher gears, or a rattle at idle that stops when you press the clutch.",
      recommendedFix: "Replace the clutch kit, and the flywheel if it has too much play.",
      costEstimate: "£600-950",
      severityGrade: 3,
      likelihood: "Medium",
      riskFactor: 46,
    },
    {
      problem: "Headlamp aim or adjuster fault",
      location: "Front lights",
      symptoms:
        "A headlamp failed on aim at the 2024 MOT. Check both headlamps on dipped and main beam, and that the height adjuster on the dashboard moves the beam.",
      recommendedFix: "Re-aim the headlamps, or replace a broken adjuster motor.",
      costEstimate: "£40-150",
      severityGrade: 2,
      likelihood: "Low",
      riskFactor: 24,
    },
  ],

  // Newest first, as the DVSA returns them.
  motHistory: [
    {
      completedDate: "2026-03-10T10:42:11.000Z",
      testResult: "PASSED",
      expiryDate: "2027-03-17",
      odometerValue: "73480",
      odometerUnit: "MI",
      odometerResultType: "READ",
      motTestNumber: "529418302231",
      dataSource: "DVSA",
      defects: [
        { text: "Nearside Front Tyre worn close to legal limit/worn on edge (5.2.3 (e))", type: "ADVISORY", dangerous: false },
        { text: "Front Brake pad(s) wearing thin (1.1.13 (a) (ii))", type: "ADVISORY", dangerous: false },
        OIL_LEAK,
      ],
    },
    {
      completedDate: "2025-03-12T14:05:37.000Z",
      testResult: "PASSED",
      expiryDate: "2026-03-17",
      odometerValue: "66215",
      odometerUnit: "MI",
      odometerResultType: "READ",
      motTestNumber: "418830276514",
      dataSource: "DVSA",
      defects: [
        {
          text: "Offside Rear Suspension arm pin or bush worn but not resulting in excessive movement (5.3.4 (a) (i))",
          type: "ADVISORY",
          dangerous: false,
        },
        OIL_LEAK,
      ],
    },
    {
      completedDate: "2024-03-14T09:31:02.000Z",
      testResult: "PASSED",
      expiryDate: "2025-03-17",
      odometerValue: "59003",
      odometerUnit: "MI",
      odometerResultType: "READ",
      motTestNumber: "307162945820",
      dataSource: "DVSA",
      defects: [OIL_LEAK],
    },
    {
      completedDate: "2024-03-08T11:18:45.000Z",
      testResult: "FAILED",
      expiryDate: null,
      odometerValue: "58964",
      odometerUnit: "MI",
      odometerResultType: "READ",
      motTestNumber: "307159218847",
      dataSource: "DVSA",
      defects: [
        { text: "Offside Rear Tyre tread depth below requirements of 1.6mm (5.2.3 (e))", type: "MAJOR", dangerous: false },
        { text: "Nearside Headlamp aim too high (4.1.2 (a) (i))", type: "MAJOR", dangerous: false },
        OIL_LEAK,
      ],
    },
    {
      completedDate: "2023-03-13T15:47:20.000Z",
      testResult: "PASSED",
      expiryDate: "2024-03-17",
      odometerValue: "51337",
      odometerUnit: "MI",
      odometerResultType: "READ",
      motTestNumber: "196604731152",
      dataSource: "DVSA",
      defects: [
        OIL_LEAK,
        {
          text: "Front Brake disc worn, pitted or scored, but not seriously weakened (1.1.14 (a) (ii))",
          type: "ADVISORY",
          dangerous: false,
        },
      ],
    },
    {
      completedDate: "2022-03-09T10:12:54.000Z",
      testResult: "PASSED",
      expiryDate: "2023-03-17",
      odometerValue: "43918",
      odometerUnit: "MI",
      odometerResultType: "READ",
      motTestNumber: "885213460978",
      dataSource: "DVSA",
      defects: [OIL_LEAK],
    },
    {
      completedDate: "2021-03-15T13:26:09.000Z",
      testResult: "PASSED",
      expiryDate: "2022-03-17",
      odometerValue: "36442",
      odometerUnit: "MI",
      odometerResultType: "READ",
      motTestNumber: "774390125663",
      dataSource: "DVSA",
      defects: [],
    },
    {
      completedDate: "2020-03-11T08:58:33.000Z",
      testResult: "PASSED",
      expiryDate: "2021-03-17",
      odometerValue: "31870",
      odometerUnit: "MI",
      odometerResultType: "READ",
      motTestNumber: "663018457392",
      dataSource: "DVSA",
      defects: [
        { text: "Nearside Front Tyre worn close to legal limit/worn on edge (5.2.3 (e))", type: "ADVISORY", dangerous: false },
      ],
    },
    {
      completedDate: "2019-03-14T12:40:16.000Z",
      testResult: "PASSED",
      expiryDate: "2020-03-17",
      odometerValue: "24105",
      odometerUnit: "MI",
      odometerResultType: "READ",
      motTestNumber: "551972638404",
      dataSource: "DVSA",
      defects: [],
    },
  ],
};
